import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Award, FileText, Target, BookOpen, TrendingUp,
  Phone, Mail, Instagram, MessageCircle, ChevronDown, ChevronUp,
  Calendar, Clock, CheckCircle2, Loader2, AlertCircle, Check, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const classTimings = [
  { grade: 'Grade 2', time: '2:00–3:00 PM', days: 'Monday, Wednesday, Friday' },
  { grade: 'Grade 4', time: '3:00–4:00 PM', days: 'Monday, Wednesday, Friday' },
  { grade: 'Grade 5', time: '4:00–5:00 PM', days: 'Monday, Wednesday, Friday' },
  { grade: 'Grade 6', time: '5:00–6:00 PM', days: 'Monday, Wednesday, Friday' },
  { grade: 'Grade 7', time: '6:00–7:00 PM', days: 'Monday, Wednesday, Friday' },
  { grade: 'Grade 8', time: '7:00–8:00 PM', days: 'Monday, Wednesday, Friday' },
];

const features = [
  {
    icon: <Users className="w-8 h-8 text-blue-600" />,
    title: 'Small Batch Sizes',
    description: 'Maximum 10-12 students per batch for personalized attention'
  },
  {
    icon: <Target className="w-8 h-8 text-blue-600" />,
    title: 'Focused Attention',
    description: 'Small groups ensure each student gets their doubts cleared'
  },
  {
    icon: <FileText className="w-8 h-8 text-blue-600" />,
    title: 'Practice Worksheets',
    description: 'Regular worksheets for concept reinforcement'
  },
  {
    icon: <Award className="w-8 h-8 text-blue-600" />,
    title: 'Regular Tests',
    description: 'Periodic assessments to track progress'
  },
  {
    icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
    title: 'Olympiad Focus',
    description: 'Special preparation for mathematical olympiads'
  },
  {
    icon: <BookOpen className="w-8 h-8 text-blue-600" />,
    title: 'Strong Fundamentals',
    description: 'Building clear concepts and mathematical thinking'
  },
];

const faqs = [
  {
    question: 'How are classes conducted?',
    answer: 'All classes are conducted live online via Google Meet. Students can interact directly with the teacher, ask questions in real-time, and participate in discussions.'
  },
  {
    question: 'What is the batch size?',
    answer: 'We maintain small batch sizes of 10-12 students maximum to ensure every child gets personalized attention and the teacher can address individual doubts.'
  },
  {
    question: 'Are worksheets provided?',
    answer: 'Yes! We provide regular practice worksheets after each class to reinforce concepts. Additional worksheets are also available for download on our website.'
  },
  {
    question: 'Are classes live or recorded?',
    answer: 'All classes are conducted live to ensure real-time interaction and doubt-clearing. Currently, we do not provide recorded videos as we focus on live engagement and personalized learning.'
  },
  {
    question: 'Is Olympiad preparation available?',
    answer: 'Yes, our curriculum includes Olympiad-level problem-solving and logical reasoning questions to prepare students for competitions like IMO, NSTSE, and other math olympiads.'
  },
  {
    question: 'How do I register for a demo class?',
    answer: 'Simply fill out the demo registration form on this page with your details and preferred time slot.'
  },
];

function Home() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    parent_name: '',
    student_name: '',
    grade: '',
    school_name: '',
    phone: '',
    email: '',
    city: '',
    message: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedGradeTiming, setSelectedGradeTiming] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll to section when navigating from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.state]);

  // Close mobile menu when clicking a link
  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId, closeMobileMenu = false) => {
    if (closeMobileMenu) {
      setMobileMenuOpen(false);
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    fetchAvailableDates();
  }, []);

  const fetchAvailableDates = async () => {
    try {
      const response = await axios.get(`${API}/demo-slots/available-dates`);
      setAvailableDates(response.data.available_dates || []);
    } catch (error) {
      console.error('Failed to fetch available dates:', error);
    }
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    
    // If grade is already selected, fetch the specific slot
    if (formData.grade) {
      await fetchSlotForGrade(date, formData.grade);
    }
  };

  const fetchSlotForGrade = async (date, grade) => {
    if (!date || !grade) return;
    
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/demo-slots/times?date=${date}&grade=${encodeURIComponent(grade)}`);
      const slots = response.data || [];
      setTimeSlots(slots);
      
      // Auto-select the slot if available
      if (slots.length > 0 && slots[0].status === 'available') {
        setSelectedSlot(slots[0]);
      } else if (slots.length > 0 && slots[0].status === 'booked') {
        setSelectedSlot(null);
        toast.error(`The slot for ${grade} on this date is already booked. Please try another date.`);
      }
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
      toast.error('Failed to load time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleGradeChange = (grade) => {
    setFormData({ ...formData, grade });
    const timing = classTimings.find(t => t.grade === grade);
    setSelectedGradeTiming(timing || null);
    setSelectedSlot(null);
    
    // If date is already selected, fetch the slot for this grade
    if (selectedDate) {
      fetchSlotForGrade(selectedDate, grade);
    }
    
    // Clear grade error on selection
    if (errors.grade) {
      setErrors(prev => ({ ...prev, grade: '' }));
    }
  };

  // Validation patterns
  const validationPatterns = {
    name: /^[A-Za-z ]{2,50}$/,
    phone: /^[6-9]\d{9}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    school: /^[A-Za-z0-9 .,&()\-']{2,100}$/,
    city: /^[A-Za-z ]{2,50}$/
  };

  // Capitalize first letter of each word
  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  // Format phone number with space (98765 43210)
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return digits;
  };

  // Validate individual field
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'parent_name':
        if (!value.trim()) {
          error = 'Parent name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (!validationPatterns.name.test(value.trim())) {
          error = 'Only alphabets and spaces allowed';
        }
        break;
        
      case 'student_name':
        if (!value.trim()) {
          error = 'Student name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (!validationPatterns.name.test(value.trim())) {
          error = 'Only alphabets and spaces allowed';
        }
        break;
        
      case 'school_name':
        if (!value.trim()) {
          error = 'School name is required';
        } else if (value.trim().length < 2) {
          error = 'School name must be at least 2 characters';
        } else if (!validationPatterns.school.test(value.trim())) {
          error = 'Invalid characters in school name';
        }
        break;
        
      case 'phone':
        const cleanPhone = value.replace(/\D/g, '');
        if (!cleanPhone) {
          error = 'Phone number is required';
        } else if (cleanPhone.length !== 10) {
          error = 'Phone number must be 10 digits';
        } else if (!validationPatterns.phone.test(cleanPhone)) {
          error = 'Invalid Indian mobile number (must start with 6-9)';
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!validationPatterns.email.test(value.trim())) {
          error = 'Please enter a valid email address';
        }
        break;
        
      case 'city':
        if (!value.trim()) {
          error = 'City is required';
        } else if (value.trim().length < 2) {
          error = 'City must be at least 2 characters';
        } else if (!validationPatterns.city.test(value.trim())) {
          error = 'Only alphabets and spaces allowed';
        }
        break;
        
      case 'grade':
        if (!value) {
          error = 'Please select a grade';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ['parent_name', 'student_name', 'grade', 'school_name', 'phone', 'email', 'city'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Scroll to first error field
  const scrollToFirstError = (errorFields) => {
    const fieldOrder = ['parent_name', 'student_name', 'grade', 'school_name', 'phone', 'email', 'city'];
    for (const field of fieldOrder) {
      if (errorFields[field]) {
        const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          break;
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Apply formatting based on field type
    switch (name) {
      case 'parent_name':
      case 'student_name':
        // Allow only alphabets and spaces, auto-capitalize
        processedValue = value.replace(/[^A-Za-z ]/g, '');
        processedValue = capitalizeWords(processedValue);
        break;
        
      case 'phone':
        // Format phone number
        processedValue = formatPhone(value);
        break;
        
      case 'city':
        // Allow only alphabets and spaces, auto-capitalize
        processedValue = value.replace(/[^A-Za-z ]/g, '');
        processedValue = capitalizeWords(processedValue);
        break;
        
      case 'email':
        // Convert to lowercase
        processedValue = value.toLowerCase();
        break;
        
      default:
        break;
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Validate field if it's been touched
    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // Handle field blur (mark as touched and validate)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      // Check if already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.onload = () => resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    ['parent_name', 'student_name', 'grade', 'school_name', 'phone', 'email', 'city'].forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);
    
    // Validate all fields
    if (!validateForm()) {
      const currentErrors = {};
      ['parent_name', 'student_name', 'grade', 'school_name', 'phone', 'email', 'city'].forEach(field => {
        const error = validateField(field, formData[field]);
        if (error) currentErrors[field] = error;
      });
      scrollToFirstError(currentErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    if (!selectedSlot) {
      toast.error('Please select a date and time slot');
      // Scroll to slot selection
      document.querySelector('[data-testid="demo-date-selector"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Check for duplicate registration (same phone number)
      const cleanPhone = formData.phone.replace(/\D/g, '');
      try {
        const duplicateCheck = await axios.get(`${API}/demo-slots/check-duplicate?phone=${cleanPhone}`);
        if (duplicateCheck.data.exists) {
          toast.error("You've already registered with this phone number. We'll contact you shortly! 😊", {
            duration: 5000
          });
          setIsSubmitting(false);
          return;
        }
      } catch (dupError) {
        // If endpoint doesn't exist, continue with booking
        console.log('Duplicate check not available, proceeding...');
      }

      // First, create the booking
      const bookingResponse = await axios.post(`${API}/demo-slots/book`, {
        slot_id: selectedSlot.slot_id,
        ...formData,
        phone: formData.phone.replace(/\D/g, '') // Send clean phone number
      });

      const booking = bookingResponse.data;

      // Load Razorpay
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error('Payment gateway failed to load');
        setIsSubmitting(false);
        return;
      }

      // Create payment order
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        amount: 20000, // ₹200 in paise
        currency: 'INR',
        receipt: `demo_${booking.booking_id}`,
        notes: {
          booking_id: booking.booking_id,
          student_name: formData.student_name,
          demo_date: selectedSlot.date,
          demo_time: selectedSlot.time
        }
      });

      const { order_id, amount, currency, key_id } = orderResponse.data;
      
      console.log('Razorpay Order Created:', { order_id, amount, currency, key_id });

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'Dive Into Mathematics',
        description: `Demo Class - ${selectedSlot.date} at ${selectedSlot.time}`,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            // Complete demo slot payment
            await axios.post(`${API}/payments/demo-slot/complete`, null, {
              params: {
                booking_id: booking.booking_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id
              }
            });

            toast.success(`Demo class booked! Check ${formData.email} for confirmation.`);
            
            // Reset form
            setFormData({
              parent_name: '',
              student_name: '',
              grade: '',
              school_name: '',
              phone: '',
              email: '',
              city: '',
              message: ''
            });
            setErrors({});
            setTouched({});
            setSelectedDate('');
            setSelectedSlot(null);
            setTimeSlots([]);
            setSelectedGradeTiming(null);
            fetchAvailableDates(); // Refresh available dates
            
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Payment verification error:', error);
          }
        },
        prefill: {
          name: formData.parent_name,
          email: formData.email,
          contact: formData.phone.replace(/\D/g, '')
        },
        theme: {
          color: '#1E3A8A'
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled');
            setIsSubmitting(false);
          },
          escape: false,
          backdropclose: false
        }
      };

      console.log('Opening Razorpay with options:', options);
      
      const paymentObject = new window.Razorpay(options);
      
      // Handle payment failure
      paymentObject.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error?.description || response.error?.reason || 'Please try again'}`);
        setIsSubmitting(false);
      });
      
      paymentObject.open();
      setIsSubmitting(false);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed. Please try again.');
      console.error(error);
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' });
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="sticky-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-xl sm:text-2xl font-bold text-blue-900 hover:text-blue-700 transition-colors cursor-pointer">
              Dive Into Mathematics
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-6">
              <button onClick={() => scrollToSection('timings')} className="text-slate-600 hover:text-blue-900 transition-colors cursor-pointer bg-transparent border-none">Class Timings</button>
              <button onClick={() => scrollToSection('demo-form')} className="text-slate-600 hover:text-blue-900 transition-colors cursor-pointer bg-transparent border-none">Demo Class</button>
              <Link to="/worksheets" className="text-slate-600 hover:text-blue-900 transition-colors">Worksheets</Link>
              <Link to="/workshops" className="text-slate-600 hover:text-blue-900 transition-colors">Workshops</Link>
              <button onClick={() => scrollToSection('about')} className="text-slate-600 hover:text-blue-900 transition-colors cursor-pointer bg-transparent border-none">About Us</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-blue-900 transition-colors cursor-pointer bg-transparent border-none">Contact</button>
            </nav>
            
            {/* Desktop Buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="text-blue-900 hover:bg-blue-50 text-sm" 
                data-testid="parent-login-btn"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/parent/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
              >
                Parent Portal
              </Button>
              <Link to="/login">
                <Button variant="outline" className="text-blue-900 border-blue-900 hover:bg-blue-50 text-sm" data-testid="admin-login-btn">
                  Admin Login
                </Button>
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-slate-600 hover:text-blue-900 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-200 pt-4 fade-in-up" style={{ animationDuration: '0.2s' }}>
              <nav className="flex flex-col space-y-3" aria-label="Mobile navigation">
                <button 
                  onClick={() => scrollToSection('timings', true)}
                  className="text-left text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                  aria-label="View class timings"
                >
                  Class Timings
                </button>
                <button 
                  onClick={() => scrollToSection('demo-form', true)}
                  className="text-left text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                  aria-label="Book a demo class"
                >
                  Demo Class
                </button>
                <Link 
                  to="/worksheets" 
                  className="text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                  onClick={handleMobileNavClick}
                >
                  Worksheets
                </Link>
                <Link 
                  to="/workshops" 
                  className="text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                  onClick={handleMobileNavClick}
                >
                  Workshops
                </Link>
                <button 
                  onClick={() => scrollToSection('about', true)}
                  className="text-left text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                >
                  About Us
                </button>
                <button 
                  onClick={() => scrollToSection('contact', true)}
                  className="text-left text-slate-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors font-medium"
                >
                  Contact
                </button>
                
                {/* Mobile Portal Buttons */}
                <div className="flex flex-col gap-2 pt-3 border-t border-slate-200 mt-2">
                  <Button 
                    variant="ghost" 
                    className="w-full text-blue-900 hover:bg-blue-50 justify-center"
                    onClick={() => {
                      handleMobileNavClick();
                      const redirectUrl = window.location.origin + '/parent/dashboard';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                  >
                    Parent Portal
                  </Button>
                  <Link to="/login" onClick={handleMobileNavClick}>
                    <Button variant="outline" className="w-full text-blue-900 border-blue-900 hover:bg-blue-50 justify-center">
                      Admin Login
                    </Button>
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-left" style={{ animationFillMode: 'forwards' }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 badge-pulse inline-block px-3 py-1 bg-blue-50 rounded-full">Grade 2 - Grade 8</p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
                Live Online Maths Classes Your Child Will <span className="gradient-text">Actually Love</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-2">
                Join <span className="font-bold text-blue-900">500+ students</span> already excelling in Maths! 10-12 students per batch, expert teachers, weekly worksheets & Olympiad prep.
              </p>
              <p className="text-lg mb-8">
                <span className="text-amber-600 font-semibold">Limited seats per batch</span> - don't let your child miss out!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToForm}
                  className="btn-primary bg-blue-900 hover:bg-blue-800 text-white rounded-full px-6 sm:px-8 py-6 font-semibold text-sm sm:text-base group whitespace-nowrap"
                  data-testid="book-demo-btn-hero"
                  aria-label="Book a demo class for 200 rupees"
                >
                  Book Demo @ ₹200
                  <span className="ml-2 group-hover:translate-x-1 transition-transform inline-block" aria-hidden="true">→</span>
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => document.getElementById('timings').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-blue-900 border-2 border-blue-200 rounded-full px-6 sm:px-8 py-6 font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 text-sm sm:text-base whitespace-nowrap"
                  data-testid="view-timings-btn"
                  aria-label="View class timings schedule"
                >
                  View Class Timings
                </Button>
              </div>
              {/* Trust indicators */}
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Demo Fee Adjusted in 1st Month</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>Small Batch (10-12 Students)</span>
                </div>
              </div>
            </div>
            <div className="fade-in-right image-hover rounded-3xl overflow-hidden shadow-2xl" style={{ animationFillMode: 'forwards' }}>
              {/* Optimized hero image */}
              <div className="relative bg-gradient-to-br from-blue-100 to-slate-100" style={{ aspectRatio: '3/2' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_dive-math-demo/artifacts/ileu4h4e_ChatGPT%20Image%20Apr%204%2C%202026%2C%2002_30_05%20PM.png"
                  alt="Students learning mathematics together online"
                  className="w-full h-full object-cover"
                  width="600"
                  height="400"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Below fold, add lazy-section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50 lazy-section" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 text-center mb-4">WHY CHOOSE US</p>
          <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-12">
            <span className="gradient-text">What Makes Us Different</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`feature-card bg-white rounded-2xl border border-slate-200 shadow-sm p-8 opacity-0 fade-in-up stagger-${index + 1}`}
                style={{ animationFillMode: 'forwards' }}
                data-testid={`feature-card-${index}`}
              >
                <div className="icon-wrapper mb-4 inline-block p-3 bg-blue-50 rounded-xl">{feature.icon}</div>
                <h4 className="text-lg font-semibold text-slate-800 mb-2">{feature.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Timings */}
      <section className="py-16 md:py-24 lazy-section" id="timings">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-6">
            <span className="gradient-text">Class Timings by Grade</span>
          </h3>
          
          {/* Session Details - Prominent Info Box */}
          <div className="max-w-3xl mx-auto mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="p-3">
                <div className="text-3xl font-bold text-blue-900">3</div>
                <div className="text-sm text-slate-600 font-medium">Sessions per Week</div>
              </div>
              <div className="p-3 border-y sm:border-y-0 sm:border-x border-blue-200">
                <div className="text-3xl font-bold text-blue-900">1 Hour</div>
                <div className="text-sm text-slate-600 font-medium">Each Session</div>
              </div>
              <div className="p-3">
                <div className="text-3xl font-bold text-blue-900">₹1500</div>
                <div className="text-sm text-slate-600 font-medium">for 12 Sessions</div>
              </div>
            </div>
          </div>
          
          {/* Pan-India & Schedule Notice */}
          <div className="max-w-3xl mx-auto mb-10 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 p-2 rounded-lg shrink-0 mt-0.5">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900 mb-1">Pan-India Classes with Fixed Schedule</h4>
                <p className="text-sm text-amber-800 leading-relaxed">
                  We teach students from <strong>across India</strong>, and to ensure consistency for all learners, 
                  our class timings and days remain <strong>fixed throughout the academic year</strong>. 
                  Changing the schedule for individual requests would disrupt the learning of other students in the batch.
                </p>
                <p className="text-sm text-amber-800 leading-relaxed mt-2">
                  Please join <strong>2-3 minutes before</strong> the scheduled time to ensure a smooth start. 
                  Late entries may miss important concepts covered at the beginning of the class.
                </p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-slate-500 mb-8">Click on your grade to book a demo class</p>
          
          {/* Grade Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {classTimings.map((timing, index) => (
              <div 
                key={index} 
                onClick={() => {
                  setFormData({ ...formData, grade: timing.grade });
                  setSelectedGradeTiming(timing);
                  document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' });
                }}
                className="timing-card bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-blue-300 cursor-pointer group transition-all"
                data-testid={`timing-card-${timing.grade.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-bold text-blue-900 group-hover:text-blue-700 transition-colors">{timing.grade}</h4>
                  <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-900 transition-colors">
                    <Clock className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center text-slate-700">
                    <Clock className="w-4 h-4 mr-3 text-blue-500" />
                    <span className="font-semibold text-lg">{timing.time}</span>
                  </div>
                  <div className="flex items-center text-slate-600">
                    <Calendar className="w-4 h-4 mr-3 text-blue-400" />
                    <span>{timing.days}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <span className="text-blue-600 font-medium text-sm group-hover:text-blue-800 flex items-center">
                    Book Demo for {timing.grade}
                    <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Registration Form */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white lazy-section" id="demo-form">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-10 px-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
              <div className="relative">
                <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">Limited Slots Available</span>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">Register for a Demo Class</h3>
                <p className="text-blue-100">Experience our teaching methodology firsthand</p>
                <div className="mt-4 bg-white/10 rounded-lg p-3 max-w-md mx-auto">
                  <p className="text-white text-sm font-medium">💰 Demo Fee: ₹200</p>
                  <p className="text-blue-200 text-xs mt-1">✓ Adjusted in 1st month fees if you enroll</p>
                  <p className="text-blue-200 text-xs">✓ Kept as demo fee if you don't join</p>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6" data-testid="demo-registration-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="parent_name" className="flex items-center gap-1">
                    Parent Name *
                    {touched.parent_name && !errors.parent_name && formData.parent_name && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input 
                      id="parent_name"
                      name="parent_name"
                      value={formData.parent_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter parent's full name"
                      data-testid="demo-parent-name"
                      className={`rounded-lg ${errors.parent_name && touched.parent_name ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                  </div>
                  {errors.parent_name && touched.parent_name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.parent_name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="student_name" className="flex items-center gap-1">
                    Student Name *
                    {touched.student_name && !errors.student_name && formData.student_name && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input 
                      id="student_name"
                      name="student_name"
                      value={formData.student_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter student's full name"
                      data-testid="demo-student-name"
                      className={`rounded-lg ${errors.student_name && touched.student_name ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                  </div>
                  {errors.student_name && touched.student_name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.student_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="grade" className="flex items-center gap-1">
                    Grade *
                    {formData.grade && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <Select 
                    value={formData.grade} 
                    onValueChange={handleGradeChange}
                  >
                    <SelectTrigger 
                      data-testid="demo-grade"
                      className={errors.grade && touched.grade ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Grade 2', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8'].map(grade => (
                        <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.grade && touched.grade && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.grade}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school_name" className="flex items-center gap-1">
                    School Name *
                    {touched.school_name && !errors.school_name && formData.school_name && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input 
                      id="school_name"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter school name"
                      data-testid="demo-school-name"
                      className={`rounded-lg ${errors.school_name && touched.school_name ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                  </div>
                  {errors.school_name && touched.school_name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.school_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Show fixed timing when grade is selected */}
              {selectedGradeTiming && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="fixed-timing-display">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Your Regular Class Timing (after demo):</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center text-blue-800">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="font-medium">{selectedGradeTiming.time}</span>
                    </div>
                    <div className="flex items-center text-blue-800">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm">{selectedGradeTiming.days}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-1">
                    Phone Number *
                    {touched.phone && !errors.phone && formData.phone && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="98765 43210"
                      data-testid="demo-phone"
                      className={`rounded-lg ${errors.phone && touched.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    <MessageCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" title="WhatsApp enabled" />
                  </div>
                  {errors.phone && touched.phone && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.phone}
                    </p>
                  )}
                  {!errors.phone && touched.phone && formData.phone && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" /> We'll send confirmation via WhatsApp
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1">
                    Email Address *
                    {touched.email && !errors.email && formData.email && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="parent@gmail.com"
                      data-testid="demo-email"
                      className={`rounded-lg ${errors.email && touched.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city" className="flex items-center gap-1">
                  City *
                  {touched.city && !errors.city && formData.city && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                </Label>
                <div className="relative">
                  <Input 
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Mumbai"
                    data-testid="demo-city"
                    className={`rounded-lg ${errors.city && touched.city ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                </div>
                {errors.city && touched.city && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {errors.city}
                  </p>
                )}
              </div>

              {/* Demo Slot Selection */}
              <div className="space-y-4 p-5 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  Select Demo Date & Time *
                </h4>
                
                {/* Date Selection */}
                <div className="space-y-3">
                  <Label className="text-slate-600">Choose Date</Label>
                  <div className="flex flex-wrap gap-3" data-testid="demo-date-selector">
                    {availableDates.length === 0 ? (
                      <div className="flex items-center gap-2 text-slate-500">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading available dates...</span>
                      </div>
                    ) : (
                      availableDates.map(date => (
                        <button
                          key={date}
                          type="button"
                          onClick={() => handleDateChange(date)}
                          className={`slot-button px-5 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-300 ${
                            selectedDate === date 
                              ? 'bg-blue-900 text-white border-blue-900 shadow-lg shadow-blue-900/25 scale-105' 
                              : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                          }`}
                          data-testid={`demo-date-${date}`}
                        >
                          {formatDateDisplay(date)}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Time Slot Selection - Only shown when both date AND grade are selected */}
                {selectedDate && formData.grade && (
                  <div className="space-y-3 fade-in-up" style={{ animationDuration: '0.3s' }}>
                    <Label className="text-slate-600">Your Demo Time Slot</Label>
                    {loadingSlots ? (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Checking availability...</span>
                      </div>
                    ) : timeSlots.length === 0 ? (
                      <p className="text-sm text-slate-500">Please select a grade first to see your time slot</p>
                    ) : (
                      <div data-testid="demo-time-selector">
                        {timeSlots.map(slot => (
                          <div
                            key={slot.slot_id}
                            className={`p-4 rounded-xl border-2 ${
                              slot.status === 'booked'
                                ? 'bg-red-50 border-red-200 text-red-700'
                                : selectedSlot?.slot_id === slot.slot_id
                                  ? 'bg-green-50 border-green-400 text-green-800'
                                  : 'bg-blue-50 border-blue-200 text-blue-800'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5" />
                                <div>
                                  <p className="font-bold text-lg">{slot.time}</p>
                                  <p className="text-sm opacity-80">{formData.grade} Demo Class</p>
                                </div>
                              </div>
                              {slot.status === 'booked' ? (
                                <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm font-medium">Already Booked</span>
                              ) : (
                                <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm font-medium flex items-center gap-1">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Available
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Prompt to select grade if only date is selected */}
                {selectedDate && !formData.grade && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
                    <p>Please select a grade above to see your demo time slot.</p>
                  </div>
                )}

                {/* Selected Slot Display */}
                {selectedSlot && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl fade-in-scale" data-testid="selected-slot-display">
                    <div className="flex items-center gap-3 text-green-800">
                      <div className="bg-green-100 p-2 rounded-full success-check">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="font-semibold text-lg">
                        Demo scheduled for {formatDateDisplay(selectedSlot.date)} at {selectedSlot.time}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message / Notes (Optional)</Label>
                <Textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  data-testid="demo-message"
                  className="rounded-xl border-2 border-slate-200 focus:border-blue-400 transition-colors"
                  placeholder="Any specific topics or concerns you'd like us to address?"
                />
              </div>

              {/* Payment Info */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5" data-testid="payment-info">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <span className="text-amber-600 text-lg">₹</span>
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900">Demo Fee: ₹200 (100% Refundable)</p>
                    <p className="text-sm text-amber-700 mt-1">This amount will be adjusted in your first month's fees if you join our academic batch.</p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting || !selectedSlot}
                className="btn-primary w-full bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full px-8 py-7 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
                data-testid="demo-submit-btn"
                aria-label="Pay 200 rupees and book demo class"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                    Processing Payment...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Pay ₹200 & Book Demo Class
                    <span className="group-hover:translate-x-1 transition-transform" aria-hidden="true">→</span>
                  </span>
                )}
              </Button>
              
              {/* Security badges */}
              <div className="flex items-center justify-center gap-4 text-xs text-slate-400 pt-2">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
                  Secure Payment
                </span>
                <span>•</span>
                <span>100% Refundable</span>
                <span>•</span>
                <span>Powered by Razorpay</span>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50/30 lazy-section" id="about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="image-hover rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_dive-math-demo/artifacts/8dwf404x_ChatGPT%20Image%20Apr%204%2C%202026%2C%2002_45_25%20PM.png"
                alt="Expert mathematics teacher"
                className="rounded-2xl shadow-xl w-full h-auto"
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ aspectRatio: '3/2', objectFit: 'cover' }}
              />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">ABOUT US</p>
              <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                Building Mathematical Confidence in Small Group Batches
              </h3>
              <div className="space-y-4 text-base text-slate-600 leading-relaxed">
                <p>
                  Dive Into Mathematics is a specialized online learning platform dedicated to making mathematics 
                  accessible, enjoyable, and impactful for students from Grades 1 to 8.
                </p>
                <p>
                  We believe that every child can excel in mathematics with the right guidance, personalized attention, 
                  and a supportive learning environment. Our small batch sizes of 10-12 students ensure that each student receives 
                  individual focus, helping them build strong fundamentals and develop confidence in problem-solving.
                </p>
                <p>
                  Through live interactive group classes, regular practice worksheets, and Olympiad-focused training, 
                  we nurture mathematical thinking and help students achieve their full potential.
                </p>
                <div className="flex items-start mt-6 space-y-2 flex-col">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-slate-800">Expert Mathematics Teachers</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-slate-800">Small Batches (10-12 Students)</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-slate-800">Live Interactive Group Sessions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 text-center mb-4">FAQ</p>
          <h3 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-12">
            <span className="gradient-text">Frequently Asked Questions</span>
          </h3>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border border-slate-200 px-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-blue-900 py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section / Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-xl font-bold mb-4 block hover:text-blue-300 transition-colors">
                Dive Into Mathematics
              </Link>
              <p className="text-slate-400 text-sm">
                Personalised online mathematics classes for students from Grade 2 to Grade 8.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><button onClick={() => scrollToSection('timings')} className="hover:text-white transition-colors">Class Timings</button></li>
                <li><button onClick={() => scrollToSection('demo-form')} className="hover:text-white transition-colors">Demo Class</button></li>
                <li><Link to="/worksheets" className="hover:text-white transition-colors">Worksheets</Link></li>
                <li><Link to="/workshops" className="hover:text-white transition-colors">Workshops</Link></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Get in Touch</h5>
              <div className="space-y-3">
                <a href="tel:+917021405430" className="flex items-center text-sm text-slate-400 hover:text-white transition-colors" data-testid="contact-phone">
                  <Phone className="w-4 h-4 mr-2" />
                  +91 70214 05430
                </a>
                <a href="mailto:Diveintomathematic@gmail.com" className="flex items-center text-sm text-slate-400 hover:text-white transition-colors" data-testid="contact-email">
                  <Mail className="w-4 h-4 mr-2" />
                  Diveintomathematic@gmail.com
                </a>
                <div className="flex space-x-4 pt-2">
                  <a href="https://wa.me/917021405430" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors" data-testid="contact-whatsapp">
                    <MessageCircle className="w-5 h-5" />
                  </a>
                  <a href="https://www.instagram.com/diveintomathematics/?hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-pink-400 transition-colors" data-testid="contact-instagram">
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            <p>&copy; 2026 Dive Into Mathematics. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/917021405430" 
        target="_blank" 
        rel="noopener noreferrer"
        className="floating-whatsapp bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors"
        data-testid="floating-whatsapp-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
}

export default Home;
